import { defineStore } from 'pinia';
import { t } from '../composables/useText.js';
import {
  fetchPlayersPage,
  fetchPlayerSeasonStats,
  fetchPlayerSeasons
} from '../services/api';
import {
  DIFFICULTY_LEVELS,
  getDifficultyGameRange,
  getDifficultyFeatures
} from '../config/difficulty';
import { CLUBS } from '../data/data.clubs';
import { LEAGUES, leagueIdByLabel } from '../utils/leagues';
import { formatSeason, parseSeasonLabel, seasonLabels } from '../utils/season';
import { isNationalTeamStat } from '../utils/isNationalTeam';

const AGGREGATE_SAME_CLUB_STORAGE_KEY = 'gtf.aggregateSameClub';
const SELECTION_STORAGE_KEY = 'gtf.formSelections';
const GAME_MODE_VALUES = new Set(['career', 'photo', 'stats']);

const readAggregateSameClub = () => {
  if (typeof window === 'undefined' || !window.localStorage) return true;
  try {
    const stored = window.localStorage.getItem(AGGREGATE_SAME_CLUB_STORAGE_KEY);
    if (stored === null) return true;
    return stored === 'true';
  } catch (error) {
    return true;
  }
};

const writeAggregateSameClub = (value) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(AGGREGATE_SAME_CLUB_STORAGE_KEY, String(value));
  } catch (error) {
    // Ignore storage errors and keep the in-memory value.
  }
};

const normalizeSelectionValue = (value) => (typeof value === 'string' ? value : '');

const normalizeDifficultySelection = (value) => {
  const resolved = DIFFICULTY_LEVELS.find((option) => option.value === value);
  return resolved ? resolved.value : 'easy';
};

const normalizeGameModeSelection = (value) => {
  if (GAME_MODE_VALUES.has(value)) return value;
  return 'career';
};

const readSelectionState = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return {
      leagueSelection: '',
      seasonSelection: '',
      teamId: '',
      difficultySelection: 'easy',
      gameModeSelection: 'career'
    };
  }

  try {
    const stored = window.localStorage.getItem(SELECTION_STORAGE_KEY);
    if (!stored) {
      return {
        leagueSelection: '',
        seasonSelection: '',
        teamId: '',
        difficultySelection: 'easy',
        gameModeSelection: 'career'
      };
    }

    const parsed = JSON.parse(stored);
    return {
      leagueSelection: normalizeSelectionValue(parsed?.leagueSelection),
      seasonSelection: normalizeSelectionValue(parsed?.seasonSelection),
      teamId: normalizeSelectionValue(parsed?.teamId),
      difficultySelection: normalizeDifficultySelection(parsed?.difficultySelection),
      gameModeSelection: normalizeGameModeSelection(parsed?.gameModeSelection)
    };
  } catch (error) {
    return {
      leagueSelection: '',
      seasonSelection: '',
      teamId: '',
      difficultySelection: 'easy',
      gameModeSelection: 'career'
    };
  }
};

export const persistSelectionState = (state) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const payload = {
      leagueSelection: normalizeSelectionValue(state.leagueSelection),
      seasonSelection: normalizeSelectionValue(state.seasonSelection),
      teamId: normalizeSelectionValue(state.teamId),
      difficultySelection: normalizeDifficultySelection(state.difficultySelection),
      gameModeSelection: normalizeGameModeSelection(state.gameModeSelection)
    };
    window.localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // Ignore storage errors and keep the in-memory value.
  }
};

const pickRandom = (items) => items[Math.floor(Math.random() * items.length)];
const pickRandomLeagueLabel = () => pickRandom(LEAGUES)?.label || '';
const pickRandomSeasonLabel = () => pickRandom(seasonLabels()) || '';
const pickRandomSeasonLabelForClub = (leagueLabel, teamId) => {
  const leagueId = leagueIdByLabel[leagueLabel];
  if (!leagueId || !teamId) return '';
  const clubs = CLUBS[leagueId] || [];
  const club = clubs.find((entry) => String(entry?.id) === String(teamId));
  const seasons = club?.seasons || [];
  if (seasons.length === 0) return '';
  const seasonYear = pickRandom(seasons);
  return formatSeason(seasonYear);
};

const getEntryAppearances = (entry) => {
  const stats = entry?.statistics || [];
  return stats.reduce((total, stat) => total + (stat.games?.appearences ?? 0), 0);
};

const hasAppearances = (entry) => getEntryAppearances(entry) > 0;

const findPlayerEntryForSeasons = async (playerId, seasonYears) => {
  for (let index = seasonYears.length - 1; index >= 0; index -= 1) {
    const seasonYear = seasonYears[index];
    const statsData = await fetchPlayerSeasonStats(playerId, seasonYear);
    const playerEntry = statsData?.response?.[0];
    if (playerEntry && hasAppearances(playerEntry)) {
      return { playerEntry, seasonYear };
    }
  }

  return { playerEntry: null, seasonYear: null };
};

const isEntryWithinRange = (entry, range) => {
  const appearances = getEntryAppearances(entry);
  if (appearances < range.min) return false;
  if (range.max !== null && appearances > range.max) return false;
  return true;
};

const buildPlayerName = (player) => {
  if (player?.name) return player.name;
  return [player?.firstname, player?.lastname].filter(Boolean).join(' ');
};

const buildPlayerFromEntry = (entry) => {
  const playerData = entry?.player;
  const player = {
    id: playerData?.id,
    name: buildPlayerName(playerData),
    photo: playerData?.photo ?? null,
    birthDate: playerData?.birth?.date ?? null,
    nationality: playerData?.nationality ?? null,
    position: entry?.statistics?.[0]?.games?.position ?? null
  };

  if (!player.id || !player.name) return null;
  return player;
};

const sortCareerRows = (rows) => {
  return rows.sort((a, b) => {
    if (a.seasonYear !== b.seasonYear) return a.seasonYear - b.seasonYear;
    if (a.team !== b.team) return a.team.localeCompare(b.team);
    return 0;
  });
};

export const useGameStore = defineStore('game', {
  state: () => ({
    league: '',
    season: '',
    ...readSelectionState(),
    difficulty: 'easy',
    aggregateSameClub: readAggregateSameClub(),
    player: null,
    seasonYears: [],
    careerRows: [],
    revealingSeasons: [],
    loading: false,
    error: ''
  }),
  getters: {
    hasGame: (state) => Boolean(state.player) && state.careerRows.length > 0,
    difficultyFeatures: (state) => getDifficultyFeatures(state.difficulty)
  },
  actions: {
    resetGame(options = {}) {
      const { keepSelection = false } = options;
      if (!keepSelection) {
        this.league = '';
        this.season = '';
        this.leagueSelection = '';
        this.seasonSelection = '';
        this.teamId = '';
        this.gameModeSelection = 'career';
        this.difficulty = 'easy';
        this.difficultySelection = 'easy';
      }
      this.player = null;
      this.seasonYears = [];
      this.careerRows = [];
      this.revealingSeasons = [];
      this.loading = false;
      this.error = '';
    },
    setAggregateSameClub(value) {
      this.aggregateSameClub = Boolean(value);
      writeAggregateSameClub(this.aggregateSameClub);
    },
    async startGame(leagueLabel, seasonLabel, teamId = '') {
      this.loading = true;
      this.error = '';
      this.difficulty = this.difficultySelection || 'easy';

      const resolvedLeagueLabel = leagueLabel || pickRandomLeagueLabel();
      let resolvedSeasonLabel = seasonLabel;

      if (!resolvedSeasonLabel) {
        if (teamId) {
          resolvedSeasonLabel =
            pickRandomSeasonLabelForClub(resolvedLeagueLabel, teamId) ||
            pickRandomSeasonLabel();
        } else {
          resolvedSeasonLabel = pickRandomSeasonLabel();
        }
      }

      this.league = resolvedLeagueLabel;
      this.season = resolvedSeasonLabel;
      this.leagueSelection = leagueLabel;
      this.seasonSelection = seasonLabel;
      this.teamId = teamId;

      try {
        const leagueId = leagueIdByLabel[resolvedLeagueLabel];
        const seasonYear = parseSeasonLabel(resolvedSeasonLabel);

        if (!leagueId || Number.isNaN(seasonYear)) {
          throw new Error(t('store.invalid-league-season'));
        }

        const player = await this.pickRandomPlayer(leagueId, seasonYear, teamId);
        const seasonYears = await this.fetchSeasonYears(player.id);
        if (seasonYears.length === 0) {
          throw new Error(t('store.no-season-history'));
        }

        const careerRows = await this.fetchCareerRows(player.id, seasonYears);
        if (careerRows.length === 0) {
          throw new Error(t('store.no-career-stats'));
        }

        this.player = player;
        this.seasonYears = seasonYears;
        this.careerRows = careerRows;
        this.revealingSeasons = [];
      } catch (error) {
        this.error = error?.message || t('store.something-went-wrong');
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async loadGameFromPlayerId({ playerId, keepSelection = false }) {
      this.resetGame({ keepSelection });
      this.loading = true;
      this.error = '';

      try {
        const seasonYears = await this.fetchSeasonYears(playerId);
        if (seasonYears.length === 0) {
          throw new Error(t('store.no-season-history'));
        }

        const { playerEntry } = await findPlayerEntryForSeasons(playerId, seasonYears);
        if (!playerEntry) {
          throw new Error(t('store.no-eligible-stats'));
        }

        const player = buildPlayerFromEntry(playerEntry);
        if (!player) {
          throw new Error(t('store.missing-player-details'));
        }

        const careerRows = await this.fetchCareerRows(player.id, seasonYears);
        if (careerRows.length === 0) {
          throw new Error(t('store.no-career-stats-short'));
        }

        this.player = player;
        this.seasonYears = seasonYears;
        this.careerRows = careerRows;
        this.revealingSeasons = [];
      } catch (error) {
        this.error = error?.message || t('store.something-went-wrong');
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async pickRandomPlayer(leagueId, seasonYear, teamId = '') {
      const firstPage = await fetchPlayersPage(leagueId, seasonYear, 1, teamId);
      const totalPages = firstPage?.paging?.total || 1;
      const difficultyRange = getDifficultyGameRange(this.difficulty);

      const tryCandidates = (pageData) => {
        const candidates = (pageData?.response || [])
          .filter((entry) => isEntryWithinRange(entry, difficultyRange))
          .map((entry) => buildPlayerFromEntry(entry))
          .filter(Boolean);

        if (candidates.length === 0) return null;
        return pickRandom(candidates);
      };

      const pages = Array.from({ length: totalPages }, (_, index) => index + 1);
      for (let i = pages.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [pages[i], pages[j]] = [pages[j], pages[i]];
      }

      for (let index = 0; index < pages.length; index += 1) {
        const page = pages[index];
        const pageData =
          page === 1 ? firstPage : await fetchPlayersPage(leagueId, seasonYear, page, teamId);
        const chosen = tryCandidates(pageData);
        if (chosen) return chosen;
      }

      throw new Error(t('store.no-players-found'));
    },
    async revealSeason(seasonYear) {
      if (!this.player) return;
      if (this.revealingSeasons.includes(seasonYear)) return;
      if (this.careerRows.some((row) => row.seasonYear === seasonYear)) return;

      this.revealingSeasons = [...this.revealingSeasons, seasonYear];

      try {
        const seasonRows = await this.fetchSeasonRows(this.player.id, seasonYear);
        if (seasonRows.length === 0) return;
        this.careerRows = sortCareerRows([...this.careerRows, ...seasonRows]);
      } catch (error) {
        this.error = error?.message || t('store.something-went-wrong');
        throw error;
      } finally {
        this.revealingSeasons = this.revealingSeasons.filter((year) => year !== seasonYear);
      }
    },
    async fetchSeasonYears(playerId) {
      const seasonData = await fetchPlayerSeasons(playerId);
      return (seasonData?.response || []).sort((a, b) => a - b);
    },
    buildSeasonRows(seasonYear, statistics) {
      const rowsMap = new Map();

      statistics.forEach((stat) => {
        if (isNationalTeamStat(stat)) return;

        const teamName = stat.team?.name || 'Unknown';
        const teamLogo = stat.team?.logo ?? null;
        const key = `${seasonYear}::${teamName}`;
        const appearances = stat.games?.appearences ?? 0;
        const goals = stat.goals?.total ?? 0;

        if (!rowsMap.has(key)) {
          rowsMap.set(key, {
            season: formatSeason(seasonYear),
            seasonYear,
            team: teamName,
            teamLogo,
            appearances: 0,
            goals: 0
          });
        }

        const entry = rowsMap.get(key);
        if (!entry.teamLogo && teamLogo) {
          entry.teamLogo = teamLogo;
        }
        entry.appearances += appearances;
        entry.goals += goals;
      });

      return Array.from(rowsMap.values());
    },
    async fetchSeasonRows(playerId, seasonYear) {
      const statsData = await fetchPlayerSeasonStats(playerId, seasonYear);
      const playerEntry = statsData?.response?.[0];
      const statistics = playerEntry?.statistics || [];
      return this.buildSeasonRows(seasonYear, statistics);
    },
    async fetchCareerRows(playerId, seasonYears) {
      const rows = [];

      for (const seasonYear of seasonYears) {
        const seasonRows = await this.fetchSeasonRows(playerId, seasonYear);
        rows.push(...seasonRows);
      }

      return sortCareerRows(rows);
    }
  }
});
